import axios from "axios";
import { clearAuthStorage, getStoredAuthToken } from "@/utils/authStorage";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "",
  withCredentials: false,
  headers: {
    Accept: "application/json",
  },
});

const stripContentType = (headers) => {
  if (!headers) return;
  if (typeof headers.delete === "function") {
    headers.delete("Content-Type");
    headers.delete("content-type");
  }
  delete headers["Content-Type"];
  delete headers["content-type"];
  if (typeof headers.set === "function") {
    try {
      headers.set("Content-Type", undefined);
    } catch {
      /* AxiosHeaders may reject undefined in some versions */
    }
  }
};

// Request interceptor to add auth token and fix multipart uploads
api.interceptors.request.use(
  (config) => {
    const authToken = getStoredAuthToken();
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Browser must set multipart boundary itself — never force JSON/multipart
    // Content-Type when the body is FormData (public application uploads, etc.).
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      stripContentType(config.headers);
    } else if (
      config.data &&
      typeof config.data === "object" &&
      !(typeof FormData !== "undefined" && config.data instanceof FormData) &&
      !(config.data instanceof Blob) &&
      !(config.data instanceof ArrayBuffer)
    ) {
      // Default JSON bodies when caller did not set a content type.
      const existing =
        config.headers?.["Content-Type"] ||
        config.headers?.["content-type"] ||
        (typeof config.headers?.get === "function"
          ? config.headers.get("Content-Type")
          : null);
      if (!existing) {
        if (typeof config.headers?.set === "function") {
          config.headers.set("Content-Type", "application/json");
        } else {
          config.headers = {
            ...(config.headers || {}),
            "Content-Type": "application/json",
          };
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      clearAuthStorage();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    } else if (error.response?.status === 500) {
      console.error("Server error:", error);
    }

    return Promise.reject(error);
  },
);

export default api;
